import { matchPath } from 'react-router-dom'
import { DotMark24 } from '@carbon/icons-react'
import { saveAs } from 'file-saver'
import { synapse } from 'state'
import {
  compact,
  first,
  get,
  isEmpty,
  isUndefined,
  size,
  values
} from 'lodash'
import { ExportApi } from 'api/export'
import { ImportApi } from 'api/import'
import { ExportTranslationsApi, ImportTranslationsApi } from 'api/translations'
import { addNotification } from 'components/Notifications/actions'
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT
} from 'components/Settings/options'
import {
  canImport,
  canExport,
  getPermissionsValue,
  PERMISSIONS
} from 'services/auth'
import history from 'services/history'
import { DEFAULT_THEME, LOCALSTORAGE_PREFERENCES_KEY } from 'config'
import { openModal } from 'components/Modal/actions'
import { documentEvents } from 'pages/InputView/constants'
import dispatcher from 'pages/InputView/eventsDispatcher'
import { updateInstanceWorkingTableCount } from './actions'
import moment from 'moment'
import { XLS } from 'components/ExportModal/constants'

export const setLocalStoragePreferences = (newPreferences) => {
  const preferences = getLocalStoragePreferences()
  localStorage.setItem(
    LOCALSTORAGE_PREFERENCES_KEY,
    JSON.stringify({ ...preferences, ...newPreferences })
  )
}

export const getLocalStoragePreferences = (key) => {
  const maybePreferences = localStorage.getItem(LOCALSTORAGE_PREFERENCES_KEY)
  const preferences = maybePreferences ? JSON.parse(maybePreferences) : {}
  return key ? preferences[key] : preferences
}

export const getInitialTheme = () =>
  getLocalStoragePreferences('theme') || DEFAULT_THEME

export const getInitialFormattingOptions = () => {
  const { dateFormat = DEFAULT_DATE_FORMAT, timeFormat = DEFAULT_TIME_FORMAT } =
    getLocalStoragePreferences()
  return { dateFormat, timeFormat }
}

export const getInitialWrapOptions = () => {
  const { headerWrap = true, contentWrap = false } =
    getLocalStoragePreferences()
  return { headerWrap, contentWrap }
}

export const mapInstance = (instance) => ({
  ...instance,
  sidebar: true,
  sidebarTitle: instance.title,
  href: `/instances/${instance.id}/workingtables`
})

const onExportWt = (wt, t, fileFormat) => {
  ExportApi.exportWorkingTable({
    startedNotificationText: t('ExportStarted'),
    pendingNotificationText: t('ExportPending'),
    wtId: wt.id,
    fileFormat
  })
}

export const getPermission = (handler, wt, models) => {
  const modelPermissions = models[wt.type]?.permissions
  if (isEmpty(modelPermissions)) {
    return handler(wt.permission)
  } else {
    values(PERMISSIONS).forEach((permission) => {
      // if permission it's not specified in permissions object, by default it's true
      if (isUndefined(modelPermissions[permission])) {
        modelPermissions[permission] = true
      }
    })

    return handler(getPermissionsValue(modelPermissions).permission)
  }
}

export const mapWorkingTableWithT =
  (t, exportAllowed, models) => (wt, wtCounts, instanceID) => {
    const [, wtType] = wt.config.match(/value="(.*?)"/)
    const wtHref = `/instances/${instanceID}/workingtables/${wt.id}/${wtType}/documents`
    const { pathname } = history.location
    const routeMatch = matchPath(pathname, {
      path: '/instances/:i/workingtables/:w/:t/documents',
      exact: true
    })

    return {
      ...wt,
      type: wtType,
      href: wtHref,
      icon: DotMark24,
      title: wt.title,
      count: wtCounts[wt.id] || null,
      label: wt.title,
      groups: wt.groups,
      groupTitle: wt.groups,
      actions: compact([
        // canCreate(wt.permission) && {
        //   title: t('AddDocument'), // TODO: Eliminare o controllare i permessi del docment type, non della WT
        //   onClick: () => {
        //     const dispatchNewDocumentEvent = () => {
        //       dispatcher.emptyDocumentCreated({ type: wtType, activityId: wt.id })
        //     }
        //     if (routeMatch) {
        //       dispatchNewDocumentEvent()
        //     } else {
        //       const unsubscribe = synapse.subscribe(
        //         documentEvents.DOCUMENT_READY,
        //         () => {
        //           dispatchNewDocumentEvent()
        //           unsubscribe()
        //         }
        //       )
        //       history.push(wtHref)
        //     }
        //   }
        // },
        getPermission(canImport, wt, models) && {
          title: t('Import'),
          onClick: () => {
            openModal('import', {
              onSubmit: (file) =>
                ImportApi.importWorkingTable({
                  id: wt.id,
                  file,
                  startedNotificationText: t('Shell:ImportStarted'),
                  pendingNotificationText: t('Shell:ImportPending'),
                  t
                })
            })
          }
        },
        getPermission(canExport, wt, models) && {
          title: t('Export'),
          onClick: () => {
            if (
              isEmpty(exportAllowed) ||
              (exportAllowed && size(exportAllowed) > 1)
            ) {
              openModal('export', {
                exportAllowed,
                onExport: (fileFormat) => onExportWt(wt, t, fileFormat)
              })
            } else {
              onExportWt(
                wt,
                t,
                first(exportAllowed) !== XLS && first(exportAllowed)
              )
            }
          }
        }
      ])
    }
  }
export const mapTranslationsTableWithT = (t) => (wt) => {
  const wtHref = `/translations/translationTables/${wt.group}/${wt.type}/documents`
  return {
    ...wt,
    type: wt.type,
    href: wtHref,
    icon: DotMark24,
    title: wt.title,
    label: wt.title,
    groups: wt.group,
    groupTitle: wt.group,
    actions: [
      {
        title: t('Translations:Import'),
        onClick: () => {
          openModal('import', {
            onSubmit: (file) => ImportTranslationsApi.importTranslation(file)
          })
        }
      },
      {
        title: t('Translations:Export'),
        onClick: async () => {
          addNotification({ message: t('ExportStarted') })
          const nameSpace = `${wt.groups ? wt.groups : wt.group}${
            wt.type === 'MAIN' ? '' : `.${wt.type}`
          }`
          const { blob, fileName } =
            await ExportTranslationsApi.exportTranslation(nameSpace, wt.type)
          saveAs(blob, fileName || `${nameSpace}-${wt.id}-export.xlsx`)
        }
      },
      {
        title: t('Translations:NewTranslation'),
        onClick: () => {
          openModal('newTranslation', {
            type: 'LABEL',
            group: wt.group,
            subgroup: wt.type
          })
        }
      }
    ]
  }
}

export const setConfigSidebar = (extRoutes, outputConfig, scenarioConfig) => {
  const routes = [...extRoutes]

  const outputSidebar = get(outputConfig, 'sidebar', false)
  const scenarioSidebar = get(scenarioConfig, 'scenario.sidebar', false)

  const output = routes.find((route) => route.id === 'output')
  const scenario = routes.find((route) => route.id === 'scenario')

  output.sidebar = outputSidebar
  output.routes[0].sidebar = outputSidebar

  scenario.sidebar = scenarioSidebar

  return routes
}

// -------------------- Date time format -------------------- //

export const getFormatOptions = ({ label, ...rest }) => rest

export const getInitialItem = (list, format) =>
  list.find((item) => item.value === format.value)

export const formatLabel = (list) => {
  const exampleDate = moment().set({ hour: 18, minute: 0, second: 0 })
  return list.map((item) => ({
    ...item,
    label: exampleDate.format(item.value)
  }))
}
